import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import moment from 'moment';
import {Typography} from '@material-ui/core';
import {PaperWrapper} from '../../paper-wrapper/PaperWrapper';
import {TextField} from '../../text-field/TextField';
import {Button} from '../../button/Button';
import {DatePicker} from '../../pickers/DatePicker';
import {TimePicker} from '../../pickers/TimePicker';
import BodyWeightIcon from '../../../../assets/icons/side-menu-bar-logging/body-weight-icon.svg';
import {
  addWeightLogg,
  editWeightLogg,
  checkAchievement,
} from '../../../../store/actions/logging-page/logging-page-action';
import * as homepageActions from '../../../../store/actions/homepage/homepage-action';
import {selectCurrentUser} from '../../../../store/selectors/users.selectors';
import {ValidationType} from '../../../../validation/ValidationType';
import {
  getError,
  hasError,
  isFormValidToSubmit,
  IValidationRules,
  validate,
  validateField,
} from '../../../../validation/Validation';
import {
  dateTimeCombined,
  isDateSameOrBeforeCurrentDateTime,
  stringToDateTimeString,
} from '../../../../utils/date-time-utils';
import {FormType, WidgetType} from '../../../../utils/enums';
import {IWeightDto} from '../../../../dtos/IWeightDto';
// import {selectWeightLogs} from '../../../store/selectors/loggingPageSelector';

import useStyles from './body-weight-form.styles';
import useCommonStyles from '../common-for-widgets.styles';
import {DATE_PICKER_DATE_FORMAT} from '../../../../utils/constants';

interface IBodyWeightFormData {
  bodyWeight: string;
  date: string;
  time: string;
  additionalComment: string;
}

interface IBodyWeightFormProps {
  changeState: (widgetType: WidgetType, formMode: FormType) => void;
  formType?: FormType;
  data?: IWeightDto | null;
  openedFromDialog?: boolean;
  date?: string;
  isMobile: boolean;
}

const validationRules: IValidationRules = {
  bodyWeight: [
    {type: ValidationType.REQUIRED},
    {type: ValidationType.IS_DECIMAL_NUMBER, minValue: 40, maxValue: 1000, value: '1'},
  ],
  date: [{type: ValidationType.REQUIRED}],
  time: [{type: ValidationType.REQUIRED_DURATION}],
};

const BodyWeightForm: React.FC<IBodyWeightFormProps> = (props) => {
  const classes = useStyles({
    isMobile: props.isMobile,
  });
  const commonClasses = useCommonStyles({
    isMobile: props.isMobile,
  });
  const [errors, setErrors] = useState<any>({});
  const [isLoading, setIsLoading] = useState(false);
  const currentUser = useSelector(selectCurrentUser);
  // const weightLogs = useSelector(selectWeightLogs);
  const [t] = useTranslation();
  const dispatch = useDispatch();
  const formType = props.formType || FormType.NONE_MODE;

  const [data, setData] = useState<IBodyWeightFormData>({
    bodyWeight: '',
    additionalComment: '',
    date: moment().toString(),
    time: moment().toString(),
  });

  const validateFormField = (field: string) => {
    const err = validateField(data, validationRules, field);
    setErrors({
      ...errors,
      [field]: err,
    });
  };

  const validateToSubmit = () => {
    const selectedDate = dateTimeCombined(data.date, data.time);
    if (!isDateSameOrBeforeCurrentDateTime(selectedDate)) {
      return false;
    }

    const err = validate(data, validationRules);
    return isFormValidToSubmit(err);
  };

  const changeSpecificDataType = (value: string, specificType: string) => {
    setData({
      ...data,
      [specificType]: value,
    });
  };

  useEffect(() => {
    let initialObject: IBodyWeightFormData = {
      bodyWeight: '',
      additionalComment: '',
      date: props.date || moment().toString(),
      time: props.date || moment().toString(),
    };
    if (formType == FormType.EDITING_MODE) {
      initialObject = {
        bodyWeight: props.data?.value.toString() || '',
        additionalComment: props.data?.comment || '',
        date: stringToDateTimeString(props.data?.date || ''),
        time: stringToDateTimeString(props.data?.date || ''),
      };
    }

    setData(initialObject);
  }, [props.data]);

  useEffect(() => {
    const selectedDate = dateTimeCombined(data.date, data.time);
    if (!isDateSameOrBeforeCurrentDateTime(selectedDate)) {
      setErrors({
        ...errors,
        time: [{message: t('ErrorMessages.timeAfterCurrent')}],
      });
    } else {
      if (errors['time']?.length > 0) {
        setErrors({
          ...errors,
          time: [],
        });
      }
    }
  }, [data.date, data.time]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const name = e.target.name;
    setData({
      ...data,
      [name]: value,
    });
  };

  const clearForm = () => {
    setData({
      bodyWeight: '',
      additionalComment: '',
      date: moment().toString(),
      time: moment().toString(),
    });
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      const selectedDate = dateTimeCombined(data.date, data.time);
      const weightLog = {
        userId: currentUser.id,
        date: selectedDate,
        value: parseFloat(data.bodyWeight),
        comment: data.additionalComment,
        id: props.data?.id || undefined,
      };
      let res: any;
      if (!props.data) {
        res = await dispatch(addWeightLogg(weightLog));
        await dispatch(checkAchievement('logweight'));
      } else {
        res = await dispatch(editWeightLogg(weightLog));
      }
      if (res) {
        await dispatch(homepageActions.loadNotifications());
        clearForm();
        props.changeState(WidgetType.FORM_MODE, FormType.ADDING_MODE);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    clearForm();
    setErrors({});
    props.changeState(WidgetType.FORM_MODE, FormType.ADDING_MODE);
  };

  return (
    <PaperWrapper className={classes.modalContainer}>
      <div className={commonClasses.headerContainer}>
        <div className={commonClasses.headerTextImageContainer}>
          <img
            className={commonClasses.imageMargin}
            src={BodyWeightIcon}
            alt={'body-weight-icon'}
          />
          <Typography variant={'subtitle2'}>
            {formType === FormType.EDITING_MODE
              ? t('LogsPage.bodyWidget.formTitleEdit')
              : t('LogsPage.bodyWidget.formTitle')}
          </Typography>
        </div>

        {/*{!props.openedFromDialog && (*/}
        {/*  <Button*/}
        {/*    id="close-button"*/}
        {/*    variant="text"*/}
        {/*    onClick={() => {*/}
        {/*      props.changeState(WidgetType.NONE_MODE, FormType.NONE_MODE);*/}
        {/*    }}*/}
        {/*    disabled={weightLogs?.length === 0 /*|| checkIfNotLoggedToday(weightLogs)*!/*/}
        {/*  >*/}
        {/*    {t('LogsPage.bodyWidget.closeButton')}*/}
        {/*  </Button>*/}
        {/*)}*/}
      </div>

      <div className={classes.formRowContainer}>
        <TextField
          name={'bodyWeight'}
          className={commonClasses.normalTextField}
          required={true}
          label={t('LogsPage.bodyWidget.bodyWeightLabel')}
          value={data.bodyWeight}
          placeholder={t('LogsPage.bodyWidget.bodyWeightPlaceholder')}
          onChange={(event) => handleChange(event)}
          error={hasError(errors, 'bodyWeight')}
          helperText={getError(errors, 'bodyWeight')}
          onBlur={() => validateFormField('bodyWeight')}
          containerClass={classes.bodyWeight}
        />
        {/*<Switch*/}
        {/*  changeData={(value: string) => changeSpecificDataType(value, 'weightType')}*/}
        {/*  label1={WeightType.LBS}*/}
        {/*  label2={WeightType.KG}*/}
        {/*  className={classes.switcherCenter}*/}
        {/*  initialValue={data.weightType === WeightType.KG.toString()}*/}
        {/*/>*/}
        <div className={classes.formRowSubContainer}>
          <DatePicker
            name={'date'}
            label={t('LogsPage.bodyWidget.dateLabel')}
            onChange={(value: string) => changeSpecificDataType(value, 'date')}
            value={data.date}
            required={true}
            format={DATE_PICKER_DATE_FORMAT}
            error={hasError(errors, 'date')}
            maxDate={moment()}
            helperText={getError(errors, 'date')}
            className={commonClasses.datePicker}
          />
          <TimePicker
            name={'time'}
            value={data.time}
            error={hasError(errors, 'time')}
            helperText={getError(errors, 'time')}
            label={t('LogsPage.bodyWidget.timeLabel')}
            onChange={(value: string) => changeSpecificDataType(value, 'time')}
            required={true}
            className={commonClasses.timePicker}
          />
        </div>
      </div>

      {/*<TextField*/}
      {/*  name={'additionalComment'}*/}
      {/*  className={classes.wideTextField}*/}
      {/*  label={t('LogsPage.bodyWidget.additionalCommentsLabel')}*/}
      {/*  value={data.additionalComment}*/}
      {/*  placeholder={t('LogsPage.bodyWidget.additionalCommentsPlaceholder')}*/}
      {/*  onChange={(event) => handleChange(event)}*/}
      {/*/>*/}

      <div className={commonClasses.buttonRowContainer}>
        {formType == FormType.EDITING_MODE && (
          <Button
            id={'cancel-button-body-weight-form'}
            variant={'outlined'}
            className={commonClasses.outlinedBtn}
            onClick={handleCancel}
          >
            {t('Homepage.quickProfileUser.cancelButton')}
          </Button>
        )}
        <Button
          id="submit-body-weight-form-id"
          variant="contained"
          onClick={handleSubmit}
          loading={isLoading}
          disabled={!validateToSubmit() || isLoading}
        >
          {t('LogsPage.bodyWidget.submitButton')}
        </Button>
      </div>
    </PaperWrapper>
  );
};

export default BodyWeightForm;
